import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { supabase } from "../../lib/supabase";
import { useAuth } from "../../context/EnterpriseAuthContext";

const ROLE_LABELS = {
  member: "Member",
  template_creator: "Template Creator",
  account_admin: "Account Admin",
};

/**
 * Keep only well-formed invites from an invite-user "list-pending" response.
 * @param {unknown} data - Response body.
 * @returns {{accountId: string, accountName: string | null, role: string}[]} Invites.
 */
export function parsePendingInvites(data) {
  const list = data && typeof data === "object" && Array.isArray(data.invites) ? data.invites : [];
  return list
    .filter((i) => i && typeof i.accountId === "string" && i.accountId)
    .map((i) => ({
      accountId: i.accountId,
      accountName: typeof i.accountName === "string" && i.accountName.trim() ? i.accountName : null,
      role: typeof i.role === "string" ? i.role : "member",
    }));
}

/**
 * Read the error message an edge function put in its response body.
 * @param {any} error - supabase.functions.invoke error.
 * @param {string} fallback - Message when the body has none.
 * @returns {Promise<string>} Message.
 */
async function functionErrorMessage(error, fallback) {
  if (error?.context && typeof error.context.json === "function") {
    try {
      const body = await error.context.json();
      if (body?.error) return String(body.error);
    } catch {
      // Body was not JSON; use the fallback.
    }
  }
  return fallback;
}

/**
 * Shows invitations to other workspaces that an admin sent to this (already registered) user.
 * invite-user stores them server side; the user accepts or declines here. Checked once per
 * signed-in user per page load. Any failure to list (for example an older invite-user deploy
 * that does not know the action) is silent: the prompt simply does not appear.
 * @returns {JSX.Element | null} Prompt.
 */
export default function PendingInvitesPrompt() {
  const { state: authState, actions } = useAuth();
  const userId = authState.isAuthenticated ? authState.user?.id || null : null;
  const [invites, setInvites] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyAccount, setBusyAccount] = useState(null);
  const [error, setError] = useState("");
  const checkedFor = useRef(null);

  useEffect(() => {
    if (!userId || checkedFor.current === userId) return undefined;
    checkedFor.current = userId;
    let cancelled = false;
    supabase.functions
      .invoke("invite-user", { body: { action: "list-pending" } })
      .then(
        ({ data, error: invokeError }) => {
          if (cancelled || invokeError || data?.error) return;
          const found = parsePendingInvites(data);
          setInvites(found);
          setOpen(found.length > 0);
        },
        () => {}
      );
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Signing out (or switching user) drops anything shown for the previous user.
  useEffect(() => {
    if (!userId) {
      checkedFor.current = null;
      setInvites([]);
      setOpen(false);
      setError("");
    }
  }, [userId]);

  const respond = useCallback(
    async (accountId, action) => {
      setBusyAccount(accountId);
      setError("");
      try {
        const { data, error: invokeError } = await supabase.functions.invoke("invite-user", {
          body: { action, accountId },
        });
        if (invokeError) {
          throw new Error(
            await functionErrorMessage(invokeError, `Could not ${action} the invitation. Please try again.`)
          );
        }
        if (data?.error) throw new Error(String(data.error));

        const remaining = invites.filter((i) => i.accountId !== accountId);
        setInvites(remaining);
        if (remaining.length === 0) setOpen(false);

        // Reload memberships so the new workspace shows up in the switcher. The active
        // workspace is deliberately not changed.
        if (action === "accept") await actions.refreshAccountData();
      } catch (err) {
        setError(err?.message || "Something went wrong. Please try again.");
      } finally {
        setBusyAccount(null);
      }
    },
    [actions, invites]
  );

  if (!userId || invites.length === 0) return null;

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" aria-labelledby="pending-invites-title">
      <DialogTitle id="pending-invites-title">Workspace invitations</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You have been invited to join {invites.length === 1 ? "a workspace" : "these workspaces"}. Accept to
          add it to your workspace switcher. Your current workspace stays as it is.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Stack spacing={1.5}>
          {invites.map((invite) => (
            <Box
              key={invite.accountId}
              sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 }}
            >
              <Typography variant="subtitle2">{invite.accountName || "A Quill workspace"}</Typography>
              <Typography variant="caption" color="text.secondary">
                Role: {ROLE_LABELS[invite.role] || invite.role}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={() => respond(invite.accountId, "decline")}
                  disabled={busyAccount !== null}
                >
                  Decline
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => respond(invite.accountId, "accept")}
                  disabled={busyAccount !== null}
                >
                  {busyAccount === invite.accountId ? "Working..." : "Accept"}
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)}>Later</Button>
      </DialogActions>
    </Dialog>
  );
}
